import { SaveOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Form, message, Modal, Radio, Input } from 'antd'
import { FC, useEffect } from 'react'
import { translate } from '../../../core'
import { TextEditor } from '../../../core/component/TextEditor'
import { CreateBanner, GetBanners, UpdateBanner } from '../graphql/website-settings.graphql'
import { Banner, BannerCreateInput, BannerLocation } from '../type/website-settings'

type BannerEntryFormProps = {
  onClose: () => void
  open: boolean
  banner?: Banner
}

export const BannerEntryForm: FC<BannerEntryFormProps> = ({ onClose, open, banner }) => {
  const [messageApi, contextHolder] = message.useMessage()
  const [form] = Form.useForm<BannerCreateInput>()


  const [create, { loading: creating }] = useMutation(CreateBanner, {
    onCompleted: () => {
      onClose()
      messageApi.success(translate('banner_save_success_message'))
    },
    onError: error => messageApi.error(error.message),
    refetchQueries: [GetBanners],
  })

  const [update, { loading: updating }] = useMutation(UpdateBanner, {
    onCompleted: () => {
      onClose()
      messageApi.success(translate('banner_save_success_message'))
    },
    onError: error => messageApi.error(error.message),
    refetchQueries: [GetBanners],
  })

  const handleFinish = (values: BannerCreateInput) => {
    if (banner) {
      update({ variables: { input: { id: banner.id, ...values } } })
    } else {
      create({ variables: { input: values } })
    }
  }

  useEffect(() => {
    if (banner) {
      form.setFieldsValue(banner)
    }
    else {
      form.resetFields()
      form.setFieldsValue({
        name: '',
        location: BannerLocation.HOME,
        value: '',
      })
    }
  }, [banner])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={banner ? translate('edit', 'banner') : translate('create', 'banner')}
      onOk={form.submit}
      okButtonProps={{
        loading: creating || updating,
        icon: <SaveOutlined />,
      }}
      width='60%'
    >
      <Form
        layout='horizontal'
        form={form}
        onFinish={handleFinish}
      >
        <Form.Item
          name='name'
          label={translate('name')}
          rules={[{ required: true, message: translate('name_required') }]}
        >
          <Input placeholder={translate('enter_banner_name')} />
        </Form.Item>

        <Form.Item
          name='location'
          label={translate('location')}
        >
          <Radio.Group
            options={Object.values(BannerLocation).map(location => ({
              label: location,
              value: location,
            }))}
          />
        </Form.Item>

        <Form.Item
          name='value'
          label={translate('content')}
        >
          <TextEditor />
        </Form.Item>
      </Form>
      {contextHolder}
    </Modal>
  )
}